/**
 * @file configuration.js
 * @project Web-panel
 * @author Pavel Shabardin (<bigbn@mail.ru>) Wednesday, 10th July 2019 4:08:31 pm
 * @copyright 2015 - 2019 SKAT LLC, Delive LLC
 * @flow
 */
import type { iDataConnection, JSONValue, EmitterEvents, iLogger, iSettingsManager, iWindowManager, SettingsUnit } from '../../types'
import type { ID, WaterlineQuery, WaterlineWhere, ProviderMeta } from 'web-panel-essentials/types'
import type { iExposedView } from 'web-panel/types'

import * as React from 'react'
import { Cached } from 'web-panel-essentials/decorators'
import EventEmitter from 'events'
import { Injectable, Inject } from '../../serviceLocator'
import { __ } from '../../globals'
import { FORMAT, CAST } from 'web-panel-essentials/misc'
import E from 'web-panel-essentials/errors'
import Prompt from '../../views/Prompt'

@Injectable('settingsManager', true)
class SettingsManager extends EventEmitter implements iSettingsManager {
  @Inject dataConnection : iDataConnection
  @Inject windowManager: iWindowManager
  @Inject logger : iLogger

  static get EVENT () : EmitterEvents {
    return {
      DATA_CHANGED: 'change'
    }
  }

  get EVENT () : EmitterEvents {
    return SettingsManager.EVENT
  }

  constructor () {
    super()
    this.logger.namespace = 'configuration'
    this.dataConnection.onServerEvent('SettingsChanged', (args: {}) => {
      this.getValue.clear && this.getValue.clear()
      this.emit(this.EVENT.DATA_CHANGED, args)
      this.logger.warn('Settings invalidated')
    })
  }

  async meta () : Promise<ProviderMeta<SettingsUnit>> {
    return {
      value: 'name',
      displayValue: 'label',
      columns: [
        { name: 'value', column: 'value', title: __('VALUE'), format: FORMAT.String, type: 'String', width: 100 },
        { name: 'label', column: 'label', title: __('NAME'), format: FORMAT.String, type: 'String', width: 300 },
        { name: 'description', column: 'description', title: __('DESCRIPTION'), type: 'String', format: FORMAT.String, width: 400 },
        { name: 'name', column: 'name', title: __('CODE'), format: FORMAT.String, type: 'String', width: 200 }
      ]
    }
  }

  async get (query: ?WaterlineQuery) : Promise<Array<SettingsUnit>> {
    const params : JSONValue = query && query.where ? { where: query.where } : null
    const response : Array<SettingsUnit> = await this.dataConnection.get('/settings', params)
    return response
  }

  @Cached({ isPromise: true })
  async getValue (name: string, serviceId?: ID) : Promise<mixed | null> {
    let where : WaterlineWhere = { name }
    if (serviceId) where = { ...where, serviceId: CAST.Number(serviceId) }
    const [record] = await this.get({ where })
    return record ? record.value : null
  }

  async getValues (names: Array<string>, serviceId?: ID) : Promise<{}> {
    const records = await this.get({
      where: {
        or: names.map(name => {
          let value = { name: name }
          if (serviceId) value = { ...value, serviceId }
          return value
        })
      }
    })
    return records.reduce((acc, record) => ({ ...acc, [record.name]: record.value }), {})
  }

  async set (key: string, value: mixed) {
    const password = await this.askForAdministatorPassword()
    await this.dataConnection.post('/settings', { key, value, password })
  }

  askForAdministatorPassword () : Promise<string> {
    return new Promise((resolve, reject) => {
      const id = Symbol('id')
      this.windowManager.show({
        id,
        name: 'admin-password',
        label: __('ADMIN_PASSWORD'),
        title: __('ENTER_ADMIN_PASSWORD'),
        width: 360,
        icon: 'lock',
        view: <Prompt type='password' value='' />,
        buttons: [{
          name: 'cancel',
          label: __('CANCEL'),
          title: __('CANCEL'),
          action: async () => {
            this.windowManager.close(id)
            reject(E('E_CCAN-B', __('CANCELLED')))
          }
        }, {
          name: 'ok',
          label: 'OK',
          title: 'OK',
          isPrimary: true,
          action: async (form: iExposedView<string>) => {
            this.windowManager.close(id)
            const value = await form.data()
            resolve(value)
          }
        }]
      }).then(() => {
        this.windowManager.activate(id)
      })
    })
  }
}

export default SettingsManager
